/**
 * React Query hook for user apps
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth, useOrganization } from '@clerk/clerk-react';
import { queryKeys } from '@/lib/query-keys';
import { API_ENDPOINTS } from '@/lib/config';

interface App {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  domain: string;
  logoUrl: string | null;
  color: string | null;
  hasAccess: boolean;
}

interface UserAppsResponse {
  apps: App[];
  user: {
    id: string;
    email: string;
    name: string;
    organizationId: string | null;
  };
}

export const useUserApps = () => {
  const { getToken, isSignedIn } = useAuth();
  const { organization } = useOrganization();

  return useQuery({
    queryKey: [...queryKeys.user.apps(), organization?.id], // Include org ID in query key
    queryFn: async (): Promise<UserAppsResponse> => {
      const token = await getToken();

      // Send organization ID in query params
      const url = new URL(API_ENDPOINTS.userApps);
      if (!organization?.id) {
        // Si pas d'organisation, retourner un résultat vide
        return {
          apps: [],
          user: {
            id: '',
            email: '',
            name: '',
            organizationId: null,
          },
        };
      }
      console.log(' organization: ', organization);
      url.searchParams.set('organizationId', organization.id);

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch apps');
      }

      return response.json();
    },
    enabled: isSignedIn, // Only fetch if user is signed in
    staleTime: 1000 * 60 * 2, // 2 minutes (apps don't change often)
  });
};
